/**
 * PlotPlane — scroll-scrubbed stacked-plane camera (shared engine).
 *
 * One sticky stage, N full-bleed "beats". Scroll progress drives each beat's
 * camera distance (0 = at the camera, ±1 = neighbouring beat): far planes
 * scale slowly, near planes scale fast (parallax by depth), and each beat
 * cross-fades as the camera passes through it. Layers are ordinary <img>
 * plates, so the whole thing stays vector/sharp and mobile-cheap.
 *
 * Not a fork of the #308/#328 plot-plane modules (absent from this base) —
 * deliberately generic so destination/other journeys can reuse it:
 *   <PlotPlane beats={[{ id, label, layers, content, shimmer?, mist? }]} />
 *
 * Budget: transform/opacity only, no canvas/WebGL, no animated filters,
 * particles mount only while the track is on screen, and prefers-reduced-motion
 * swaps the whole rig for calm static sections.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'motion/react';

export interface PlotLayer {
  key: string;
  src: string;
  /** 0 = far backdrop · ~0.55 = mid subject · 1 = near foreground */
  depth: number;
  /** Decorative plates pass nothing (rendered aria-hidden). */
  alt?: string;
  className?: string;
  /**
   * 'cover' (default) fills the stage; 'bottom' hugs the plate to the bottom
   * edge at natural aspect (jetty, balustrade rails, boat plates).
   */
  anchor?: 'cover' | 'bottom';
}

export interface PlotBeat {
  id: string;
  /** Accessible label for the beat section. */
  label: string;
  layers: PlotLayer[];
  content: React.ReactNode;
  /** Slow water shimmer overlay — hero plates only. */
  shimmer?: boolean;
  /** Soft mist particles (approach beat only, count-capped). */
  mist?: boolean;
}

const MIST_COUNT = 12;

const MIST_SPRITES = [
  { left: '6%', top: '58%', size: 120, dur: '21s', delay: '0s', dx: '58px', dy: '-84px', op: 0.4 },
  { left: '18%', top: '72%', size: 90, dur: '25s', delay: '2.4s', dx: '-42px', dy: '-70px', op: 0.34 },
  { left: '31%', top: '64%', size: 140, dur: '19s', delay: '1.2s', dx: '36px', dy: '-96px', op: 0.42 },
  { left: '44%', top: '76%', size: 80, dur: '27s', delay: '4s', dx: '-54px', dy: '-64px', op: 0.3 },
  { left: '57%', top: '60%', size: 130, dur: '22s', delay: '0.8s', dx: '48px', dy: '-88px', op: 0.38 },
  { left: '69%', top: '74%', size: 100, dur: '24s', delay: '3.2s', dx: '-38px', dy: '-74px', op: 0.33 },
  { left: '80%', top: '62%', size: 150, dur: '20s', delay: '1.8s', dx: '44px', dy: '-92px', op: 0.4 },
  { left: '90%', top: '70%', size: 86, dur: '26s', delay: '5.1s', dx: '-50px', dy: '-66px', op: 0.31 },
  { left: '12%', top: '44%', size: 70, dur: '28s', delay: '3.8s', dx: '30px', dy: '-52px', op: 0.26 },
  { left: '38%', top: '40%', size: 64, dur: '30s', delay: '0.4s', dx: '-26px', dy: '-48px', op: 0.24 },
  { left: '62%', top: '46%', size: 76, dur: '23s', delay: '2.9s', dx: '34px', dy: '-58px', op: 0.27 },
  { left: '86%', top: '42%', size: 68, dur: '29s', delay: '1.5s', dx: '-32px', dy: '-54px', op: 0.25 },
] as const;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

/** Beat-local camera distance: 0 = plane at the camera, ±1 = neighbouring beat. */
export const planeDistance = (progress: number, index: number, count: number) =>
  count > 1 ? clamp(progress, 0, 1) * (count - 1) - index : 0;

/** Fly-through scale curve for the plane at a given depth. */
const depthScale = (q: number, depth: number) => {
  const base = q <= 0 ? 0.7 + 0.3 * (q + 1) : 1 + 0.42 * q;
  return 1 + (base - 1) * (0.34 + 0.92 * depth);
};

function useTrackInView(ref: React.RefObject<HTMLDivElement | null>) {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      rootMargin: '12% 0px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

function MistField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {MIST_SPRITES.slice(0, MIST_COUNT).map((s, i) => (
        <span
          key={i}
          className="lp-mist"
          style={
            {
              left: s.left,
              top: s.top,
              width: s.size,
              height: s.size,
              '--lp-dur': s.dur,
              '--lp-delay': s.delay,
              '--lp-dx': s.dx,
              '--lp-dy': s.dy,
              '--lp-op': s.op,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

function PlaneLayer({ layer, distance }: { layer: PlotLayer; distance: MotionValue<number> }) {
  const scale = useTransform(distance, (d) => depthScale(clamp(d, -1, 1), layer.depth));
  const y = useTransform(distance, (d) => clamp(d, -1, 1) * -(8 + 40 * layer.depth));
  const sizing =
    layer.anchor === 'bottom'
      ? 'absolute bottom-0 left-0 h-auto w-full'
      : 'h-full w-full object-cover';
  return (
    <motion.div className="absolute inset-0" style={{ scale, y }}>
      <img
        src={layer.src}
        alt={layer.alt ?? ''}
        aria-hidden={layer.alt ? undefined : true}
        draggable={false}
        decoding="async"
        className={`${sizing} ${layer.className ?? ''}`}
      />
    </motion.div>
  );
}

function PlaneContent({ children, distance }: { children: React.ReactNode; distance: MotionValue<number> }) {
  const scale = useTransform(distance, (d) => depthScale(clamp(d, -1, 1), 0.55));
  const y = useTransform(distance, (d) => clamp(d, -1, 1) * -26);
  const opacity = useTransform(distance, (d) => 1 - smoothstep(0.4, 0.78, Math.abs(d)));
  return (
    <motion.div
      className="relative z-20 flex h-full w-full items-center justify-center"
      style={{ scale, y, opacity }}
    >
      {children}
    </motion.div>
  );
}

function Plane({
  beat,
  index,
  count,
  progress,
  active,
}: {
  beat: PlotBeat;
  index: number;
  count: number;
  progress: MotionValue<number>;
  active: boolean;
}) {
  const distance = useTransform(progress, (v) => planeDistance(v, index, count));
  const opacity = useTransform(distance, (d) => 1 - smoothstep(0.45, 0.82, Math.abs(d)));
  const visibility = useTransform(opacity, (o): 'hidden' | 'visible' => (o < 0.015 ? 'hidden' : 'visible'));
  const pointerEvents = useTransform(opacity, (o) => (o < 0.5 ? 'none' : 'auto'));

  return (
    <motion.div
      role="group"
      aria-label={beat.label}
      className="absolute inset-0 overflow-hidden"
      style={{ opacity, visibility, pointerEvents, zIndex: count - index }}
    >
      {beat.layers.map((layer) => (
        <PlaneLayer key={layer.key} layer={layer} distance={distance} />
      ))}
      {beat.shimmer && active && <div className="lp-shimmer" />}
      {beat.mist && active && <MistField />}
      <div className="lp-scrim" />
      <PlaneContent distance={distance}>{beat.content}</PlaneContent>
    </motion.div>
  );
}

function StaticBeats({ beats }: { beats: PlotBeat[] }) {
  return (
    <div className="lp-root">
      {beats.map((beat) => (
        <section
          key={beat.id}
          aria-label={beat.label}
          className="relative flex min-h-[100svh] items-center overflow-hidden"
        >
          {beat.layers.map((layer) => (
            <div key={layer.key} className="absolute inset-0">
              <img
                src={layer.src}
                alt={layer.alt ?? ''}
                aria-hidden={layer.alt ? undefined : true}
                draggable={false}
                decoding="async"
                className={
                  layer.anchor === 'bottom'
                    ? `absolute bottom-0 left-0 h-auto w-full ${layer.className ?? ''}`
                    : `h-full w-full object-cover ${layer.className ?? ''}`
                }
              />
            </div>
          ))}
          <div className="lp-scrim" />
          <div className="relative z-20 flex min-h-[100svh] w-full items-center justify-center px-5 py-20">
            {beat.content}
          </div>
        </section>
      ))}
    </div>
  );
}

function ScrollStage({ beats }: { beats: PlotBeat[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const inView = useTrackInView(trackRef);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start start', 'end end'],
  });
  const [activeIndex, setActiveIndex] = useState(0);

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const i = beats.length > 1 ? Math.round(clamp(v, 0, 1) * (beats.length - 1)) : 0;
    setActiveIndex((prev) => (prev === i ? prev : i));
  });

  return (
    <div ref={trackRef} className="lp-root relative" style={{ height: `${beats.length * 100}vh` }}>
      <div className="sticky top-0 overflow-hidden" style={{ height: '100svh', backgroundColor: '#08202f' }}>
        <div className="absolute inset-0">
          {beats.map((beat, i) => (
            <Plane key={beat.id} beat={beat} index={i} count={beats.length} progress={scrollYProgress} active={inView} />
          ))}
        </div>
        <nav
          aria-hidden="true"
          className="absolute right-3 top-1/2 z-30 hidden -translate-y-1/2 flex-col items-center gap-2.5 md:flex"
        >
          {beats.map((beat, i) => (
            <span
              key={beat.id}
              className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${
                i === activeIndex ? 'bg-[#e7c98a]' : 'bg-white/30'
              }`}
            />
          ))}
        </nav>
      </div>
    </div>
  );
}

export function PlotPlane({ beats }: { beats: PlotBeat[] }) {
  const reduced = useReducedMotion() === true;

  // Split into two components so the scroll hooks only ever run in the
  // animated path — in reduced motion the track ref is never mounted and
  // useScroll({ target }) would warn "ref is defined but not hydrated".
  if (reduced) return <StaticBeats beats={beats} />;

  return <ScrollStage beats={beats} />;
}
